// MSSQL procedural local-variable completion helpers (ROUND2-C1.1).
//
// Detects when the cursor is inside a T-SQL procedural body (`CREATE
// PROCEDURE / ALTER PROCEDURE / CREATE FUNCTION / ALTER FUNCTION /
// CREATE TRIGGER / ALTER TRIGGER … AS BEGIN … END`) and emits
// `CompletionCandidate[]` for the `@var` declarations visible at the
// cursor via the T-SQL grammar's `parseProceduralBody` + `resolveScopeTSql`.
//
// This is a per-dialect-resolver-level dispatch: the orchestrator is
// unmodified; all wiring lives inside `mssqlResolver.ts` (caller) and this
// helper module.
//
// Tier mapping:
//   - Scalar `@var` / `@tablevar` locals  → tier 1 (current-statement columns)
//   - (Keyword suggestions for procedural T-SQL remain in `mssqlKeywords.ts`
//     at tier 7 — no new keyword emission here.)
//
// Perf (plan16 T2): scope resolution runs on the T-SQL parse WORKER via
// `proceduralWorkerBridge.getProceduralScope` — the main-thread block drops
// from ~5 s (full-buffer inline ANTLR parse) to ~0.5-1.1 ms. The bridge owns
// the fallback ladder (worker → inline-once-then-latch → empty) and never
// throws, so this module keeps the try/catch-free happy path; a missing bundle
// or parse failure surfaces as an empty scope and the DML-side suggestions
// still flow. This module owns only the tier / detail candidate mapping, which
// stays byte-identical to the pre-worker output.

import type { CompletionCandidate } from '@/core/engines';
import { tokenize, type Token } from '../context/tokenizer';
import { getProceduralScope } from './proceduralWorkerBridge';

const ENGINE = 'mssql' as const;

// ─── Procedural-body detection ────────────────────────────────────────────────

/**
 * Returns `true` when the cursor offset lies inside a `CREATE [OR ALTER]
 * PROCEDURE / FUNCTION / TRIGGER … AS BEGIN … END` body.
 *
 * Implementation: linear token scan from buffer start to the cursor,
 * tracking the most recent `CREATE [OR ALTER] PROCEDURE|FUNCTION|TRIGGER`
 * header followed by `AS`. Once `AS` is seen, any cursor position up to the
 * next top-level `GO` or the next `CREATE` (other than the same statement) is
 * treated as inside the body.
 *
 * Mirrors the conservative approach used by `findEnclosingTriggerHostTable`
 * in `mssqlProceduralExtras.ts`.
 */
export function isInsideProceduralBody(source: string, offset: number): boolean {
  const tokens = tokenize(source, 0, offset, ENGINE);
  // Track whether we have seen a CREATE PROC/FUNC/TRIG header followed by AS,
  // meaning the cursor might be inside the body.
  let bodyActive = false;

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (!tok || tok.start >= offset) break;

    const upper = identUpper(tok);
    if (!upper) continue;

    if (upper === 'GO') {
      bodyActive = false;
      continue;
    }

    if (upper === 'CREATE' || upper === 'ALTER') {
      // Look ahead past optional `OR ALTER` preamble.
      const afterPreamble = skipCreateOrAlterPreamble(tokens, i + 1);
      const kindTok = tokens[afterPreamble];
      const kindUpper = identUpper(kindTok);

      if (
        kindUpper === 'PROCEDURE' ||
        kindUpper === 'PROC' ||
        kindUpper === 'FUNCTION' ||
        kindUpper === 'TRIGGER'
      ) {
        // Scan forward from the keyword to find the AS token.
        const asIdx = findNextAsIdx(tokens, afterPreamble + 1);
        if (asIdx !== -1 && tokens[asIdx]!.start < offset) {
          bodyActive = true;
          i = asIdx;
          continue;
        }
        // No AS yet (or AS is after the cursor) — clear any prior body.
        bodyActive = false;
        continue;
      }

      // Non-PROC/FUNC/TRIG CREATE (e.g. CREATE TABLE, CREATE INDEX, CREATE VIEW) —
      // these can appear INSIDE a stored-procedure body (temp-table creation is
      // a very common T-SQL pattern).  Only treat them as a body terminator when
      // we are NOT already inside a body; if bodyActive is already true the
      // statement is nested inside the current proc and must not clear the flag.
      if (!bodyActive) {
        // bodyActive is already false; the explicit assignment keeps the
        // intent clear and makes future readers aware this is deliberate.
        bodyActive = false;
      }
      continue;
    }
  }

  return bodyActive;
}

/**
 * Returns `true` when the buffer's HEAD is a `CREATE [OR ALTER]
 * PROCEDURE|PROC|FUNCTION|TRIGGER` opener — i.e. the first meaningful token
 * (leading whitespace/comments skipped by the tokenizer) is `CREATE`/`ALTER`
 * and the routine keyword follows (past an optional `OR ALTER` preamble).
 *
 * This is deliberately a HEAD check, not the cursor-inside-body check
 * `isInsideProceduralBody` does: the F3 warm-up fires at editor mount/model-
 * attach where the cursor is typically at offset 0, and it must recognise a
 * fully-typed proc (whose `END`/`GO` is already past any end-of-buffer caret)
 * just as well as one mid-authoring. It reuses the same tokenizer + header
 * helpers as `isInsideProceduralBody` so opener classification stays in one
 * place (no new regex). Only the first ~4 KB is scanned — the opener, if any,
 * is always at the very head. */
export function looksLikeProceduralOpener(source: string): boolean {
  const to = Math.min(source.length, 4_096);
  const tokens = tokenize(source, 0, to, ENGINE);
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue; // skip non-keyword/ident tokens (e.g. leading `[`)
    if (upper !== 'CREATE' && upper !== 'ALTER') return false; // opens with something else
    const afterPreamble = skipCreateOrAlterPreamble(tokens, i + 1);
    const kindUpper = identUpper(tokens[afterPreamble]);
    return (
      kindUpper === 'PROCEDURE' ||
      kindUpper === 'PROC' ||
      kindUpper === 'FUNCTION' ||
      kindUpper === 'TRIGGER'
    );
  }
  return false;
}

// ─── Candidate emission ───────────────────────────────────────────────────────

/**
 * Emit `CompletionCandidate[]` for the `@var` declarations (and routine
 * signature parameters) visible at `offset` inside a T-SQL procedural body.
 *
 * Scope resolution runs on the parse worker via
 * `getProceduralScope` (with a fallback ladder to an inline parse; see
 * `proceduralWorkerBridge.ts`) — it never throws and returns an empty scope
 * when nothing is available. This module maps that scope to candidates; the
 * mapping (order, tier, detail strings) is byte-identical to the pre-worker
 * inline output.
 *
 * Routine signature parameters (CB4) — `@param` declarations live in the CREATE
 * PROCEDURE / FUNCTION signature, OUTSIDE the AS BEGIN…END body, so they are
 * not in the DECLARE-derived `locals`; the bridge surfaces them separately in
 * `routineParams`, deduped, and they are emitted first at tier 1.
 */
export async function emitMssqlProceduralLocalCandidates(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  const scope = await getProceduralScope(source, offset);

  for (const p of scope.routineParams) {
    out.push({
      label: p.name,
      insertText: p.name,
      isSnippet: false,
      kind: 'variable',
      tier: 1,
      detail: `${p.typeText} (${p.mode} parameter)`,
      filterText: p.name,
    });
  }

  for (const l of scope.locals) {
    if (l.kind === 'variable') {
      out.push({
        label: l.name,
        insertText: l.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `${l.typeText} (local variable)`,
        filterText: l.name,
      });
    } else if (l.kind === 'cursor') {
      // Cursor names (`DECLARE c CURSOR FOR ...`) — surfaced as tier-1 local
      // names. 'variable' is the closest CompletionCandidate kind; there is no
      // dedicated cursor kind in the engine-agnostic candidate type.
      out.push({
        label: l.name,
        insertText: l.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: 'cursor (local)',
        filterText: l.name,
      });
    } else {
      // Table-variable names (`DECLARE @t TABLE (...)`) — surfaced as tier-1
      // local names alongside scalar variables.
      out.push({
        label: l.name,
        insertText: l.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `table variable (${l.columnCount ?? 0} columns)`,
        filterText: l.name,
      });
    }
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function skipCreateOrAlterPreamble(tokens: readonly Token[], idx: number): number {
  if (identUpper(tokens[idx]) === 'OR' && identUpper(tokens[idx + 1]) === 'ALTER') {
    return idx + 2;
  }
  return idx;
}

/**
 * Find the next `AS` keyword token starting at `from`. Bounded to
 * the next 256 tokens to avoid scanning across unrelated statements on
 * malformed inputs (enterprise procs with many complex-typed parameters
 * commonly exceed 90-120 tokens between `PROCEDURE` and `AS`).
 */
function findNextAsIdx(tokens: readonly Token[], from: number): number {
  const limit = Math.min(tokens.length, from + 256);
  for (let i = from; i < limit; i += 1) {
    if (identUpper(tokens[i]) === 'AS') return i;
  }
  return -1;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
